(() => {
  const DAY_NAMES = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];

  const pad = n => String(n).padStart(2, '0');

  const ymd = d =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const parseDate = s => {
    if (!s) return null;

    const [y, m, d] = s.split('-').map(Number);

    return new Date(y, m - 1, d);
  };

  const startOfDay = d =>
    new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate()
    );

  const addDays = (d, n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
  };

  const mondayOf = d => {
    const x = startOfDay(d);
    const dow = (x.getDay() + 6) % 7;

    return addDays(x, -dow);
  };

  const esc = s =>
    String(s ?? '').replace(
      /[&<>'"]/g,
      c => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;'
      }[c])
    );

  const prettyTime = t => {
    if (!t) return '';

    const [h, m] = t.split(':').map(Number);

    if (Number.isNaN(h)) return t;

    const ap = h >= 12 ? 'PM' : 'AM';

    return `${h % 12 || 12}:${pad(m || 0)} ${ap}`;
  };


  /*
   * Check monthly recurrence rules.
   */
  function monthlyMatch(date, rule) {
    const y = date.getFullYear();
    const m = date.getMonth();

    if (rule === 'First_day') {
      return date.getDate() === 1;
    }

    if (rule === 'last_day') {
      return addDays(date, 1).getMonth() !== m;
    }

    const match =
      /^last_(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday)$/
        .exec(rule || '');

    if (!match) {
      return false;
    }

    return (
      DAY_NAMES[date.getDay()] === match[1] &&
      addDays(date, 7).getMonth() !== m
    );
  }


  /*
   * Determine if an event occurs on a particular date.
   */
  function occursOn(event, date) {

    const day = startOfDay(date);
    const start = parseDate(event.startDate);

    /*
     * Every event requires a Start Date.
     */
    if (!start) {
      return false;
    }


    /*
     * ONE-TIME EVENT
     *
     * Recurring = false
     *
     * The event appears only on Start Date.
     */
    if (!event.recurring) {
      return ymd(day) === ymd(start);
    }


    /*
     * Recurring events cannot appear before Start Date.
     */
    if (day < start) {
      return false;
    }


    const type =
      (event.recurrenceType || '').toLowerCase();

    const end =
      parseDate(event.endDate);


    /*
     * WEEKLY RECURRING EVENT
     *
     * End Date is OPTIONAL.
     *
     * If End Date is empty:
     *     Event continues indefinitely.
     *
     * If End Date exists:
     *     Event stops after End Date.
     */
    if (type === 'weekly') {

      if (end && day > end) {
        return false;
      }

      return (
        DAY_NAMES[day.getDay()].toLowerCase() ===
        (event.weeklyFrequency || '').toLowerCase()
      );
    }


    /*
     * MONTHLY RECURRING EVENT
     *
     * End Date IS optional.
     */
  if (type === 'monthly') {

  if (end && day > end) {
    return false;
  }

  return monthlyMatch(
    day,
    event.monthlyFrequency
  );
}


    return false;
  }


  /*
   * Initialize calendar.
   */
  function init(root) {

    const dataEl =
      root.querySelector('[data-calendar-events]');

    const grid =
      root.querySelector('[data-calendar-grid]');

    if (!dataEl || !grid) {
      return;
    }


    /*
     * Load events from Liquid JSON.
     */
    let events = [];

    try {
      events =
        JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error(
        'Event calendar data error',
        e
      );
    }


    /*
     * Start calendar on today's date.
     */
    let cursor =
      startOfDay(new Date());


    /*
     * DEFAULT VIEW
     *
     * Weekly is selected when the page loads.
     */
    let view = 'week';


    const period =
      root.querySelector('[data-calendar-period]');

    const empty =
      root.querySelector('[data-calendar-empty]');

    const gameSelect =
      root.querySelector('[data-calendar-game]');


    /*
     * Build Game filter automatically
     * from the events in the Event list collection.
     */
    if (gameSelect) {

      [
        ...new Set(
          events
            .map(e => e.game)
            .filter(Boolean)
        )
      ]
        .sort()
        .forEach(g => {

          const o =
            document.createElement('option');

          o.value = g;
          o.textContent = g;

          gameSelect.appendChild(o);
        });


      gameSelect.addEventListener(
        'change',
        render
      );
    }


    /*
     * Create event card.
     */
    function eventCard(e) {

      const image = e.image
        ? `
          <img
            src="${esc(e.image)}"
            alt=""
            loading="lazy"
            width="160"
            height="160"
          >
        `
        : '';


      return `
        <a
          class="gc-event"
          href="${esc(e.url)}"
        >

          ${image}

          <span class="gc-event__body">

            <span class="gc-event__time">
              ${esc(prettyTime(e.startTime))}
            </span>

            <span class="gc-event__name">
              ${esc(e.title)}
            </span>

            ${
              e.game
                ? `
                  <span class="gc-event__game">
                    ${esc(e.game)}
                  </span>
                `
                : ''
            }

          </span>

        </a>
      `;
    }


    /*
     * Render calendar.
     */
    function render() {

      const filter =
        gameSelect
          ? gameSelect.value
          : '';


      const source =
        filter
          ? events.filter(
              e => e.game === filter
            )
          : events;


      let first;
      let last;

      const dates = [];


      /*
       * WEEK VIEW
       */
      if (view === 'week') {

        first =
          mondayOf(cursor);

        last =
          addDays(first, 6);


        for (let i = 0; i < 7; i++) {
          dates.push(
            addDays(first, i)
          );
        }


        if (period) {
          period.textContent =
            `${first.toLocaleDateString(
              undefined,
              {
                month: 'long',
                day: 'numeric'
              }
            )} – ${
              last.toLocaleDateString(
                undefined,
                {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                }
              )
            }`;
        }

      }

      /*
       * MONTH VIEW
       */
      else {

        const monthFirst =
          new Date(
            cursor.getFullYear(),
            cursor.getMonth(),
            1
          );


        first =
          addDays(
            monthFirst,
            -monthFirst.getDay()
          );


        const monthLast =
          new Date(
            cursor.getFullYear(),
            cursor.getMonth() + 1,
            0
          );


        last =
          addDays(
            monthLast,
            6 - monthLast.getDay()
          );


        for (
          let d = new Date(first);
          d <= last;
          d = addDays(d, 1)
        ) {

          dates.push(
            new Date(d)
          );
        }


        if (period) {
          period.textContent =
            cursor.toLocaleDateString(
              undefined,
              {
                month: 'long',
                year: 'numeric'
              }
            );
        }
      }


      /*
       * Set calendar layout class.
       */
      grid.className =
        `gc-event-calendar__grid gc-event-calendar__grid--${view}`;


      /*
       * Weekday headers.
       */
      const headers =
        (
          view === 'week'
            ? [
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
                'Sunday'
              ]
            : DAY_NAMES
        )
          .map(
            n =>
              `<div class="gc-event-calendar__weekday">${n}</div>`
          )
          .join('');


      let count = 0;


      /*
       * Calendar date cells.
       */
      const cells =
        dates
          .map(d => {

            const dayEvents =
              source
                .filter(
                  e => occursOn(e, d)
                )
                .sort(
                  (a, b) =>
                    (a.startTime || '')
                      .localeCompare(
                        b.startTime || ''
                      )
                );


            count +=
              dayEvents.length;


            const outside =
              view === 'month' &&
              d.getMonth() !==
                cursor.getMonth();


            const today =
              ymd(d) ===
              ymd(new Date());


            return `
              <div
                class="
                  gc-event-calendar__day
                  ${outside ? 'is-outside' : ''}
                  ${today ? 'is-today' : ''}
                "
              >

                <div class="gc-event-calendar__date">

                  <span class="gc-event-calendar__mobile-day">
                    ${
                      d.toLocaleDateString(
                        undefined,
                        {
                          weekday: 'short'
                        }
                      )
                    }
                  </span>

                  ${d.getDate()}

                </div>


                <div class="gc-event-calendar__events">

                  ${
                    dayEvents
                      .map(eventCard)
                      .join('')
                  }

                </div>

              </div>
            `;
          })
          .join('');


      grid.innerHTML =
        headers + cells;


      /*
       * Empty calendar message.
       */
      if (empty) {
        empty.hidden =
          count > 0;
      }


      /*
       * Highlight selected Weekly / Monthly button.
       */
      root
        .querySelectorAll(
          '[data-calendar-view]'
        )
        .forEach(
          b =>
            b.classList.toggle(
              'is-active',
              b.dataset.calendarView === view
            )
        );
    }


    /*
     * Previous
     */
    root
      .querySelector(
        '[data-calendar-prev]'
      )
      ?.addEventListener(
        'click',
        () => {

          cursor =
            view === 'week'
              ? addDays(cursor, -7)
              : new Date(
                  cursor.getFullYear(),
                  cursor.getMonth() - 1,
                  1
                );

          render();
        }
      );


    /*
     * Next
     */
    root
      .querySelector(
        '[data-calendar-next]'
      )
      ?.addEventListener(
        'click',
        () => {

          cursor =
            view === 'week'
              ? addDays(cursor, 7)
              : new Date(
                  cursor.getFullYear(),
                  cursor.getMonth() + 1,
                  1
                );

          render();
        }
      );


    /*
     * Today
     */
    root
      .querySelector(
        '[data-calendar-today]'
      )
      ?.addEventListener(
        'click',
        () => {

          cursor =
            startOfDay(
              new Date()
            );

          render();
        }
      );


    /*
     * Weekly / Monthly view selector.
     */
    root
      .querySelectorAll(
        '[data-calendar-view]'
      )
      .forEach(
        b =>
          b.addEventListener(
            'click',
            () => {

              view =
                b.dataset.calendarView;

              render();
            }
          )
      );


    /*
     * Initial render.
     */
    render();
  }


  /*
   * Initialize every Event Calendar section
   * on the page.
   */
  document.addEventListener(
    'DOMContentLoaded',
    () =>
      document
        .querySelectorAll(
          '[data-event-calendar]'
        )
        .forEach(init)
  );

})();